// SEOSection.jsx - Update to match FlipbookSettings pattern

import React from 'react';
import { Globe, Link, ExternalLink, Upload, X, ImageIcon } from 'lucide-react';

export const SEOSection = ({ 
  formData, 
  onInputChange, 
  onImageUpload,
  uploadStates = {},
  errors = {},
  generatePreviewUrl,
  faviconPreview,
  ogImagePreview,
  onRemoveFavicon,
  onRemoveOgImage
}) => {
  const previewUrl = generatePreviewUrl ? generatePreviewUrl(formData.customDomain, formData.customPath) : null;

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file && onImageUpload) {
      onImageUpload(file, 'favicon');
    }
  };

  const handleOgImageChange = (e) => {
    const file = e.target.files[0];
    if (file && onImageUpload) {
      onImageUpload(file, 'ogImage');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5" />
          SEO & Custom Domain
        </h3>
        
        <div className="space-y-6">
          {/* Custom Domain Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Link className="h-4 w-4" />
              Custom Domain & Path Configuration
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={formData.customDomain || ''}
                  onChange={(e) => onInputChange('customDomain', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="photos.yourdomain.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your custom domain (without https://)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Path <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.customPath || ''}
                  onChange={(e) => onInputChange('customPath', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="gallery or wedding2024"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Path after domain (letters, numbers, hyphens only)
                </p>
              </div>
            </div>

            {/* URL Preview */}
            {previewUrl && (
              <div className="bg-white border rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Your content will be available at:</span>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  >
                    {previewUrl}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Domain Configuration Instructions */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h5 className="text-sm font-medium text-yellow-800 mb-2">Domain Configuration Guide:</h5>
              <ul className="text-xs text-yellow-700 space-y-1">
                <li>• <strong>Base Domain:</strong> Use just "photos.yourdomain.com" for the entire domain</li>
                <li>• <strong>Path-based:</strong> Use "yourdomain.com" + path "gallery" for yourdomain.com/gallery</li>
                <li>• <strong>DNS:</strong> Point your domain/subdomain to our servers in your DNS settings</li>
                <li>• <strong>SSL:</strong> HTTPS certificates are automatically managed</li>
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Title
            </label>
            <input
              type="text"
              value={formData.seoTitle || ''}
              onChange={(e) => onInputChange('seoTitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Custom title for search engines"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Description
            </label>
            <textarea
              value={formData.seoDescription || ''}
              onChange={(e) => onInputChange('seoDescription', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Description for search engines and social media"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SEO Keywords
            </label>
            <input
              type="text"
              value={formData.seoKeywords || ''}
              onChange={(e) => onInputChange('seoKeywords', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="photo, gallery, portfolio, wedding"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separate keywords with commas
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-md font-semibold text-gray-900">SEO Images</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Favicon Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Favicon
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              {faviconPreview ? (
                <div className="relative">
                  <img
                    src={faviconPreview}
                    alt="Favicon preview"
                    className="w-8 h-8 object-cover border rounded mx-auto"
                  />
                  <button
                    type="button"
                    onClick={onRemoveFavicon}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="favicon-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      <Upload className="h-4 w-4 inline mr-2" />
                      Upload Favicon
                    </label>
                    <input
                      id="favicon-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconChange}
                      className="hidden"
                    />
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ICO, PNG, or SVG format. Recommended: 32x32px
            </p>
          </div>

          {/* OG Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Open Graph Image
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              {ogImagePreview ? (
                <div className="relative">
                  <img
                    src={ogImagePreview}
                    alt="OG image preview"
                    className="w-32 h-16 object-cover border rounded mx-auto"
                  />
                  <button
                    type="button"
                    onClick={onRemoveOgImage}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div>
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="ogimage-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      <Upload className="h-4 w-4 inline mr-2" />
                      Upload Cover
                    </label>
                    <input
                      id="ogimage-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleOgImageChange}
                      className="hidden"
                    />
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Used when sharing on social media. Recommended: 1200x630px
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};